import { Box, ButtonBase, Link, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropzoneBody, {
  dropzoneSurfaceSx,
} from "global/components/CustomDropzone/DropzoneBody";
import {
  TrashIcon,
} from "../components/SignerExperienceIcons";
import tokens from "../SignerExperience.tokens";
import { signerAccentBar } from "theme/colors";
import { useEffect, useRef, useState } from "react";
import { selectUserName } from "redux/authSlice";
import { useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import { FileField, RequestFile } from "models/interfaces";
import {
  getFileExtension,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { emitFieldCommit } from "../ParticipantScreenService";

const { palette: c, modal, signaturePad: pad } = tokens;

/**
 * Signature typefaces and their preview sizes, straight from the design. Each
 * face has its own optical size so the six tiles read as one row. These are
 * ceilings — `styleFontSize` below shrinks them for longer names.
 */
const TYPE_FONTS: { font: string; size: number }[] = [
  { font: "Great Vibes", size: 34 },
  { font: "Dancing Script", size: 30 },
  { font: "Sacramento", size: 32 },
  { font: "Allura", size: 32 },
  { font: "Caveat", size: 30 },
  { font: "Montserrat", size: 22 },
];
const MAX_TYPED_LEN = 30;
/** Breathing room around a typed signature's ink, as a fraction of the image height. */
const TYPED_SIGNATURE_MARGIN = 0.08;
/** Reference size the typed name is measured at before it's scaled to fit. */
const TYPED_FONT_SIZE = 70;
/**
 * Typed-signature image height, and its fallback shape when the pad isn't
 * opened for a placed field (e.g. My Profile's saved signature).
 */
const typeCanvasWidth = 700;
const typeCanvasHeight = 180;

type TabValue ="Type" | "Draw" | "Upload";

interface customProps {
  dispatch: Function;
  selectedFile: RequestFile;
  field: FileField;
  setField: Function;
  setCurrentSignField?: Function;
  isOpen: boolean;
  setIsOpen: Function;
  handleImageUpload: Function;
  width: number;
  height: number;
  isInitialsPad?: boolean;
  currentFieldOnFocus: number;
  moveToFieldLocation: Function;
  isSelfSign?: boolean;
  /**
   * The signer already has a signed field of this type. A new image replaces the earlier one on
   * all of them (the server keeps one per signer), so the pad says so up front.
   */
  replacesSignedFields?: boolean;
}

const SignaturePad = (props: customProps) => {
  const userName = useAppSelector(selectUserName);
  const { selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );
  const [isDraw, setisDraw] = useState<boolean>(false);
  const [tabValue, setTabValue] = useState<TabValue>("Type");
  const [isImagePreview, setIsImagePreview] = useState<boolean>(false);
  const [typedName, setTypedName] = useState<string>("");
  const [selectedFont, setSelectedFont] = useState<string>(
    TYPE_FONTS[0].font,
  );
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const typeCanvasRef = useRef<HTMLCanvasElement>(null);

  let ctx: any;
  let writingMode = false;
  let writingMode1 = false;
  const myCanvas = useRef<HTMLCanvasElement>(null);

  const knownName = (
    (selectedParticipant?.name as string) ||
    (userName as unknown as string) ||
    ""
  ).trim();
  /**
   * What the Type input starts with — the signer's own name (or its initials
   * on the initials pad), so the previewed tiles are adoptable as-is.
   */
  const defaultTypedName = (
    props.isInitialsPad
      ? knownName
          .split(/\s+/)
          .filter(Boolean)
          .map((word) => word[0].toUpperCase())
          .join("")
      : knownName
  ).slice(0, MAX_TYPED_LEN);

  // The design's segmented control is label-only — no glyphs.
  const availableTabs: TabValue[] = [
    "Type",
    "Draw",
    ...(props.isInitialsPad ? [] : (["Upload"] as TabValue[])),
  ];
  /** The value the style tiles preview; only shown filled when nothing is known. */
  const effectiveTypedName =
    typedName || (props.isInitialsPad ? "Your Initials" : "Your Name");

  /**
   * Tiles are a fixed width, so a long name at the design's size ellipsises.
   * Scale the preview down past the reference length instead of truncating it.
   */
  const styleFontSize = (base: number) => {
    const scale = Math.min(
      1,
      Math.max(
        pad.styleFitMinScale,
        pad.styleFitReferenceLength / Math.max(effectiveTypedName.length, 1),
      ),
    );
    return `${Math.round(base * scale)}px`;
  };

  useEffect(() => {
    if (tabValue === "Draw") loadPage();
  }, [tabValue, isDraw]);

  // Some callers keep the pad mounted between opens, so seed on open rather
  // than in useState's initializer.
  useEffect(() => {
    if (props.isOpen && tabValue === "Type" && !typedName) {
      setTypedName(defaultTypedName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  useEffect(() => {
    if (tabValue !== "Type") return;
    const canvas = typeCanvasRef.current;
    if (!canvas) return;
    const ctx2d = canvas.getContext("2d");
    if (!ctx2d) return;
    ctx2d.clearRect(0, 0, canvas.width, canvas.height);
    const text = typedName.trim();
    if (!text) {
      props.setField({ ...props.field, value: "" });
      return;
    }

    const font = `400 ${TYPED_FONT_SIZE}px "${selectedFont}"`;
    let cancelled = false;
    const paint = () => {
      if (cancelled) return;
      // The field stretches the image to its own w × h, so the image must
      // already have the field's proportions or the name gets squashed. Size
      // the canvas to that ratio, then scale the name to the largest size
      // whose ink fits inside the margin: short names fill the height, long
      // names fill the width and sit centred.
      const aspect =
        props.field?.w > 0 && props.field?.h > 0
          ? props.field.w / props.field.h
          : typeCanvasWidth / typeCanvasHeight;
      // Resizing a canvas resets its context state — set everything after.
      canvas.height = typeCanvasHeight;
      canvas.width = Math.round(typeCanvasHeight * aspect);
      ctx2d.font = font;
      const metrics = ctx2d.measureText(text);
      const left = metrics.actualBoundingBoxLeft;
      const ascent = metrics.actualBoundingBoxAscent;
      const inkWidth = left + metrics.actualBoundingBoxRight;
      const inkHeight = ascent + metrics.actualBoundingBoxDescent;
      const margin = canvas.height * TYPED_SIGNATURE_MARGIN;
      const scale = Math.min(
        (canvas.width - margin * 2) / Math.max(inkWidth, 1),
        (canvas.height - margin * 2) / Math.max(inkHeight, 1),
      );

      ctx2d.font = `400 ${TYPED_FONT_SIZE * scale}px "${selectedFont}"`;
      ctx2d.textAlign = "left";
      ctx2d.textBaseline = "alphabetic";
      ctx2d.fillStyle = c.signatureInk;
      ctx2d.fillText(
        text,
        (canvas.width - inkWidth * scale) / 2 + left * scale,
        (canvas.height - inkHeight * scale) / 2 + ascent * scale,
      );
      props.setField({ ...props.field, value: canvas.toDataURL("image/png") });
    };

    // Canvas silently rasterizes a fallback face if the webfont hasn't loaded
    // yet, which would bake the wrong signature into the document. Wait for the
    // face, then paint.
    if (document.fonts?.load) {
      document.fonts
        .load(font, text)
        .then(paint)
        .catch(paint);
    } else {
      paint();
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedName, selectedFont, tabValue]);

  const loadPage = () => {
    if (myCanvas.current) {
      ctx = myCanvas.current.getContext("2d");
      if (ctx) {
        ctx.lineWidth = pad.canvasStrokeWidth;
        ctx.lineJoin = ctx.lineCap = "round";
        ctx.strokeStyle = c.signatureInk;
      }
    }
  };

  const clearPad = () => {
    if (myCanvas.current) {
      ctx = myCanvas.current.getContext("2d");
      ctx?.clearRect(0, 0, myCanvas.current.width, myCanvas.current.height);
    }
    props.setField({ ...props.field, value: "" });
  };

  const handleOnDone = () => {
    const img = props.field.value;
    if (isTruthy(img)) {
      props.setIsOpen(false);
      setIsImagePreview(false);
      props.dispatch({
        type: "UPDATE_FIELD",
        fileId: props.selectedFile.unsignedPdf,
        fieldId: props.field.name,
        value: img,
      });
      // One image per signer is what gets stamped on the PDF, so carry the new one onto every
      // field of this type the signer has already signed (this one included, now that it's set).
      props.dispatch({
        type: "REPLACE_SIGNED_IMAGES",
        fieldType: props.isInitialsPad ? "initials" : "signature",
        signerIndex: props.field.signerIndex,
        value: img,
      });
      emitFieldCommit();
      if (props.isInitialsPad) {
        props.dispatch({
          type: "UPDATE_INITIALS_IMAGE",
          initialsImage: img,
        });
      } else {
        props.dispatch({
          type: "UPDATE_SIGN_IMAGE",
          signImage: img,
        });
      }
      props.handleImageUpload(img, props.isInitialsPad);
    }
  };

  const handleTabChange = (next: TabValue) => {
    if (next === tabValue) return;
    setTabValue(next);
    props.setField({ ...props.field, value: "" });
    setIsImagePreview(false);
    setisDraw(false);
    setTypedName(next === "Type" ? defaultTypedName : "");
    setUploadedFileName("");
  };

  const handleClearActiveTab = () => {
    if (tabValue === "Draw") {
      clearPad();
      setisDraw(false);
    } else if (tabValue === "Type") {
      setTypedName("");
      props.setField({ ...props.field, value: "" });
    } else if (tabValue === "Upload") {
      setIsImagePreview(false);
      setUploadedFileName("");
      props.setField({ ...props.field, value: "" });
    }
  };

  // ------ Mouse events ------
  const getCursorPosition = (event: any) => {
    const canvas = event.target as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const positionX = (event.clientX - rect.left) * scaleX;
    const positionY = (event.clientY - rect.top) * scaleY;
    return [positionX, positionY];
  };
  const handleMouseMove = (event: any) => {
    if (!writingMode) return;
    const [positionX, positionY] = getCursorPosition(event);
    ctx?.lineTo(positionX, positionY);
    ctx?.stroke();
  };
  const handleMouseDown = (event: any) => {
    loadPage();
    writingMode = true;
    ctx?.beginPath();
    const [positionX, positionY] = getCursorPosition(event);
    ctx?.moveTo(positionX, positionY);
  };
  const handleMouseUp = () => {
    const img: any = myCanvas.current?.toDataURL("image/png");
    props.setField({ ...props.field, value: img });
    setisDraw(true);
    setIsImagePreview(false);
    writingMode = false;
  };
  const handleDragEnd = () => {
    setisDraw(true);
    writingMode = false;
  };

  // ------ Touch events ------
  const getTouchPosition = (event: any) => {
    const canvas = event.target as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const positionX = (event.touches[0].clientX - rect.left) * scaleX;
    const positionY = (event.touches[0].clientY - rect.top) * scaleY;
    return [positionX, positionY];
  };
  const handleTouchStart = (event: any) => {
    loadPage();
    writingMode1 = true;
    ctx.beginPath();
    const [positionX, positionY] = getTouchPosition(event);
    ctx.moveTo(positionX, positionY);
  };
  const handleTouchEnd = () => {
    const img: any = myCanvas.current?.toDataURL("image/png");
    props.setField({ ...props.field, value: img });
    setisDraw(true);
    setIsImagePreview(false);
    writingMode1 = false;
  };
  const handleTouchMove = (event: any) => {
    if (!writingMode1) return;
    const [positionX, positionY] = getTouchPosition(event);
    ctx?.lineTo(positionX, positionY);
    ctx?.stroke();
  };

  // ------ Upload ------
  // Widened to `File[]` so the shared `CustomDropzone` (which hands back an
  // array) and the legacy `FileList` callers can both use it unchanged.
  const handleFileList = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!["jpeg", "jpg", "png"].includes(getFileExtension(file.name))) {
      openErrorNotification(
        "Document needs to be in .jpeg or .jpg or .png format",
      );
      return;
    }
    if (Math.round(file.size / 1024) > 10240) {
      openErrorNotification("File size cannot be more than 10Mb");
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const url: any = reader.result;
      props.setField({ ...props.field, value: url });
      setUploadedFileName(
        `${file.name} · ${Math.max(1, Math.round(file.size / 1024))} KB`,
      );
      setIsImagePreview(true);
    };
  };

  const handleClose = () => {
    props.setField({ ...props.field, value: "" });
    props.dispatch({
      type: "UPDATE_FIELD",
      fileId: props.selectedFile.unsignedPdf,
      fieldId: props.field.name,
      value: "",
    });
    props.setIsOpen(false);
    setIsImagePreview(false);
    setTypedName("");
  };

  // ------ Render helpers ------
  const noun = props.isInitialsPad ? "initials" : "signature";

  const renderTabs = () => (
    <Box sx={{ p: pad.tabsPadding }}>
      <Box
        role="group"
        aria-label={`Choose how to provide your ${noun}`}
        sx={{
          display: "flex",
          gap: pad.tabsGap,
          p: pad.tabsTrackPadding,
          borderRadius: pad.tabsTrackRadius,
          backgroundColor: pad.tabsTrackBg,
        }}
      >
        {availableTabs.map((id) => {
          const isActive = tabValue === id;
          return (
            <ButtonBase
              key={id}
              onClick={() => handleTabChange(id)}
              aria-pressed={isActive}
              sx={{
                flex: 1,
                height: pad.tabHeight,
                borderRadius: pad.tabRadius,
                backgroundColor: isActive ? c.white : "transparent",
                boxShadow: isActive ? pad.tabActiveShadow : "none",
                color: isActive ? c.ink : c.meta,
                fontFamily: "inherit",
                fontSize: pad.tabFontSize,
                fontWeight: pad.tabFontWeight,
                transition: "background-color .15s ease, color .15s ease",
                "&.Mui-focusVisible": {
                  outline: `2px solid ${c.accent}`,
                  outlineOffset: "2px",
                },
              }}
            >
              {id}
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );

  /** Shared small outlined button — "Clear" on Draw, "Remove" on Upload. */
  const ghostButtonSx = {
    height: pad.ghostHeight,
    px: pad.ghostPaddingX,
    gap: pad.ghostGap,
    borderRadius: pad.ghostRadius,
    border: `${pad.ghostBorderWidth} solid ${c.border}`,
    backgroundColor: c.white,
    color: c.meta,
    fontFamily: "inherit",
    fontSize: pad.ghostFontSize,
    fontWeight: 700,
    "&:hover": { color: c.danger, borderColor: c.dangerBorder },
    "&.Mui-focusVisible": {
      outline: `2px solid ${c.accent}`,
      outlineOffset: "2px",
    },
  };

  const renderDrawTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Box
        sx={{
          position: "relative",
          border: `${pad.canvasBorderWidth} solid ${c.border}`,
          borderRadius: pad.canvasRadius,
          overflow: "hidden",
        }}
      >
        <canvas
          ref={myCanvas}
          aria-label={`Draw your ${noun}. Prefer the Type or Upload tab if you cannot use a pointer.`}
          width={pad.canvasBufferWidth}
          height={pad.canvasBufferHeight}
          style={{
            display: "block",
            width: "100%",
            height: pad.canvasHeight,
            cursor: "crosshair",
            touchAction: "none",
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleDragEnd}
          onMouseOut={handleDragEnd}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
        />
        {!isDraw && (
          <Typography
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              textAlign: "center",
              fontSize: pad.canvasHintFontSize,
              color: c.disabled,
              pointerEvents: "none",
            }}
          >
            Draw your {noun} here
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ButtonBase onClick={handleClearActiveTab} sx={ghostButtonSx}>
          <TrashIcon size={pad.ghostIconSize} />
          Clear
        </ButtonBase>
      </Box>
    </Box>
  );

  const renderTypeTab = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: pad.typeGap }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          height: pad.inputHeight,
          px: pad.inputPaddingX,
          border: `${pad.inputBorderWidth} solid ${c.border}`,
          borderRadius: pad.inputRadius,
        }}
      >
        <Box
          component="input"
          value={typedName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setTypedName(e.target.value.slice(0, MAX_TYPED_LEN))
          }
          placeholder={`Type your ${props.isInitialsPad ? "initials" : "name"}`}
          aria-label={`Type your ${props.isInitialsPad ? "initials" : "name"}`}
          maxLength={MAX_TYPED_LEN}
          autoFocus
          sx={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "none",
            fontFamily: "inherit",
            fontSize: pad.inputFontSize,
            fontWeight: pad.inputFontWeight,
            color: c.ink,
            "&::placeholder": { color: c.placeholder },
          }}
        />
        <Typography
          sx={{
            fontSize: pad.counterFontSize,
            fontWeight: 700,
            color: c.accent,
            flexShrink: 0,
          }}
        >
          {typedName.length} / {MAX_TYPED_LEN}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
          },
          gap: pad.styleGridGap,
        }}
      >
        {TYPE_FONTS.map(({ font, size }) => {
          const isActive = selectedFont === font;
          return (
            <ButtonBase
              key={font}
              onClick={() => setSelectedFont(font)}
              aria-pressed={isActive}
              // The visible sample (the typed name) must be part of the name (2.5.3).
              aria-label={`${effectiveTypedName}, ${font} style`}
              sx={{
                minHeight: pad.styleMinHeight,
                p: pad.stylePadding,
                borderRadius: pad.styleRadius,
                border: `${pad.styleBorderWidth} solid ${
                  isActive ? c.accent : c.border
                }`,
                backgroundColor: c.white,
                transition: "border-color .15s ease",
                "&:hover": { borderColor: c.accent },
                "&.Mui-focusVisible": {
                  outline: `2px solid ${c.accent}`,
                  outlineOffset: "2px",
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: `"${font}"`,
                  fontSize: styleFontSize(size),
                  lineHeight: 1.2,
                  color: c.signatureInk,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {effectiveTypedName}
              </Typography>
            </ButtonBase>
          );
        })}
      </Box>

      <canvas
        ref={typeCanvasRef}
        width={typeCanvasWidth}
        height={typeCanvasHeight}
        style={{ display: "none" }}
      />
    </Box>
  );

  const renderUploadTab = () => {
    if (isImagePreview && props.field?.value) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Box
            sx={{
              border: `${pad.previewBorderWidth} solid ${c.accent}`,
              borderRadius: pad.previewRadius,
              backgroundColor: c.hover,
              p: pad.previewPadding,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={props.field.value}
              alt={`${noun} preview`}
              sx={{
                maxWidth: "100%",
                maxHeight: pad.previewMaxHeight,
                objectFit: "contain",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Typography
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: pad.fileNameFontSize,
                fontWeight: 600,
                color: c.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {uploadedFileName}
            </Typography>
            <ButtonBase onClick={handleClearActiveTab} sx={ghostButtonSx}>
              Remove
            </ButtonBase>
          </Box>
        </Box>
      );
    }
    // Same surface as the Dashboard and every other upload in the app.
    return (
      <CustomDropzone
        uniqueId={`signature-upload-${props.isInitialsPad ? "initials" : "sign"}`}
        innerBody={
          <DropzoneBody
            title={`Drop your ${noun} image here`}
            formatsText="JPEG, JPG, PNG up to 10 MB · transparent background works best"
          />
        }
        sx={dropzoneSurfaceSx()}
        onAdd={(files: File[]) => handleFileList(files)}
        maxFileSize={10 * 1024 * 1024}
        acceptedFiles={[".jpeg", ".jpg", ".png"]}
        showAlerts={false}
        multipleFileUpload={false}
      />
    );
  };

  const renderBody = () => (
    <Box
      sx={{
        p: pad.bodyPadding,
        minHeight: pad.bodyMinHeight,
        boxSizing: "border-box",
      }}
    >
      {tabValue === "Type" && renderTypeTab()}
      {tabValue === "Draw" && renderDrawTab()}
      {tabValue === "Upload" && renderUploadTab()}
    </Box>
  );

  const renderFooter = () => {
    const canSign = isTruthy(props.field.value);
    return (
      <Box
        sx={{
          p: pad.footerPadding,
          display: "flex",
          flexDirection: "column",
          gap: pad.footerGap,
        }}
      >
        <Typography
          sx={{
            fontSize: pad.legalFontSize,
            lineHeight: pad.legalLineHeight,
            color: c.muted,
            textAlign: "center",
          }}
        >
          {strings.PRIVACY_POLICY}{" "}
          <Link
            target="_blank"
            href={urls.tNcUrl}
            sx={{ color: "signer.orange.dark", fontWeight: 600 }} // accent is 4.32:1 here (1.4.3)
          >
            Terms of use
          </Link>{" "}
          ·{" "}
          <Link
            target="_blank"
            href={urls.privacyUrl}
            sx={{ color: "signer.orange.dark", fontWeight: 600 }} // accent is 4.32:1 here (1.4.3)
          >
            Privacy policy
          </Link>
        </Typography>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <ButtonBase
            onClick={handleClose}
            sx={{
              flex: "none",
              height: pad.buttonHeight,
              px: pad.cancelPaddingX,
              borderRadius: pad.buttonRadius,
              border: `1.5px solid ${c.border}`,
              backgroundColor: c.white,
              color: c.body,
              fontFamily: "inherit",
              fontSize: pad.cancelFontSize,
              fontWeight: 700,
              "&:hover": { backgroundColor: c.hover },
              "&.Mui-focusVisible": {
                outline: `2px solid ${c.accent}`,
                outlineOffset: "2px",
              },
            }}
          >
            Cancel
          </ButtonBase>
          <ButtonBase
            onClick={handleOnDone}
            disabled={!canSign}
            sx={{
              flex: 1,
              height: pad.buttonHeight,
              borderRadius: pad.buttonRadius,
              // Same brand red outside an embed; follows the partner's colour inside one.
              backgroundColor: (t) =>
                canSign ? t.palette.signer.orange.main : c.inactive,
              color: c.white,
              fontFamily: "inherit",
              fontSize: pad.submitFontSize,
              fontWeight: 700,
              cursor: canSign ? "pointer" : "not-allowed",
              "&:hover": {
                backgroundColor: (t) =>
                  canSign ? t.palette.signer.orange.dark : c.inactive,
              },
              "&.Mui-disabled": { color: c.white },
              "&.Mui-focusVisible": {
                outline: `2px solid ${c.ink}`,
                outlineOffset: "2px",
              },
            }}
          >
            Adopt &amp; sign
          </ButtonBase>
        </Box>
      </Box>
    );
  };

  return (
    // The signer surface keeps its own accent bar, width and button fills; the
    // shell, scrim, header, footer bar and responsive behaviour come from the
    // app-wide Modal System.
    <AppModal
      open={props.isOpen}
      onClose={handleClose}
      title={`Adopt your ${noun}`}
      subtitle={
        props.replacesSignedFields
          ? `This replaces your ${noun} on every field you've already signed.`
          : "Confirm how your name looks on the document."
      }
      hideIcon
      width={modal.width}
      disableBodyPadding
      topSlot={
        <Box
          aria-hidden
          sx={{ height: modal.accentBarHeight, background: signerAccentBar }}
        />
      }
      footer={renderFooter()}
      // Self-sign renders the pad over a draggable field surface; the pointer
      // events have to stop at the overlay or the field moves underneath it.
      rootPointerHandlers={
        props.isSelfSign
          ? {
              onMouseDown: (e) => e.stopPropagation(),
              onTouchEnd: (e) => e.stopPropagation(),
              onPointerDown: (e) => e.stopPropagation(),
            }
          : undefined
      }
    >
      {renderTabs()}
      {renderBody()}
    </AppModal>
  );
};

export default SignaturePad;
